<template>
  <main class="leader-page">
    <rh-cabecalho
      pagina="inicio"
      :mostrar-apresentacao="false"
      :unidade-id="unidadeId"
      :empresas="empresas"
      :mostrar-pessoas="true"
      @empresa-selecionada="selecionarEmpresa"
    />

    <b-message v-if="erro" type="is-danger" aria-close-label="Fechar" @close="erro = ''">{{ erro }}</b-message>
    <b-loading :active="loading" :is-full-page="false"></b-loading>

    <section class="welcome">
      <div class="welcome-intro">
        <p class="welcome-kicker">SEU ESPAÇO DE LIDERANÇA</p>
        <h1>Olá, {{ primeiroNome }}<span>.</span></h1>
        <p>Acompanhe sua equipe, celebre avanços e descubra onde apoiar o próximo passo.</p>
      </div>
      <div v-if="equipeAtual" class="welcome-team">
        <span class="team-icon"><b-icon icon="account-group-outline" /></span>
        <div>
          <small>{{ equipes.length > 1 ? 'EQUIPE SELECIONADA' : 'SUA EQUIPE' }}</small>
          <strong>{{ equipeAtual.nome }}</strong>
          <span>{{ equipeAtual.area }}</span>
        </div>
      </div>
    </section>

    <div v-if="!loading && !empresas.length" class="empty-state">
      Nenhuma empresa vinculada ao seu acesso. Peça ao administrador para configurar sua unidade.
    </div>

    <section v-if="unidadeId && equipesCarregadas && !equipes.length" class="unlinked-state">
      <span class="unlinked-icon"><b-icon icon="account-group-outline" size="is-large" /></span>
      <p class="eyebrow">VÍNCULO PENDENTE</p>
      <h2>Você ainda não lidera uma equipe nesta empresa.</h2>
      <p>Ninguém vinculou você como liderança a nenhuma equipe. Peça à diretoria para atribuir sua equipe em Estrutura. Assim que o vínculo for feito, seus indicadores aparecerão aqui.</p>
    </section>

    <template v-if="equipeAtual">
      <div v-if="equipes.length > 1" class="team-selector">
        <b-field label="Equipe que você acompanha">
          <b-select v-model="equipeId" expanded @input="carregarEquipe">
            <option v-for="equipe in equipes" :key="equipe.id" :value="equipe.id">{{ equipe.nome }} · {{ equipe.area }}</option>
          </b-select>
        </b-field>
      </div>

      <section v-if="dadosCarregados" class="collection-card">
        <div class="collection-copy">
          <p class="collection-kicker">{{ cicloAberto ? (cicloAberto.ativo ? 'COLETA EM ANDAMENTO' : 'CICLO PROGRAMADO') : 'DIAGNÓSTICO DA EQUIPE' }}</p>
          <h2>{{ cicloAberto ? cicloAberto.nome : 'Sua equipe está pronta para o próximo ciclo.' }}</h2>
          <p v-if="cicloAberto">
            <template v-if="cicloAberto.ativo">{{ cicloAberto.respostas }} {{ cicloAberto.respostas === 1 ? 'resposta recebida' : 'respostas recebidas' }} · encerra em {{ dataCurta(cicloAberto.fimEm) }}.</template>
            <template v-else>A coleta começa em {{ dataCurta(cicloAberto.inicioEm) }}.</template>
            <span v-if="minimoRespostas">O mínimo de divulgação é {{ minimoRespostas }} respostas, após o encerramento.</span>
          </p>
          <p v-else>Abra um diagnóstico para ouvir a equipe e acompanhar a evolução.</p>
        </div>
        <div v-if="cicloAberto && cicloAberto.ativo && minimoRespostas" class="collection-progress">
          <div class="progress-track"><span :style="{ width: progressoMinimo + '%' }"></span></div>
          <small>{{ Math.min(cicloAberto.respostas, minimoRespostas) }} de {{ minimoRespostas }} respostas para o mínimo</small>
        </div>
        <router-link class="collection-action" to="/rh/diagnostico">{{ cicloAberto ? 'Acompanhar ciclo' : 'Abrir ciclo' }} <b-icon icon="arrow-right" size="is-small" /></router-link>
      </section>

      <section v-if="dadosCarregados" class="overview-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">VISÃO GERAL</p>
            <h2>Saúde da equipe</h2>
            <p>Indicadores da equipe {{ equipeAtual.nome }}.</p>
          </div>
          <span v-if="ultimoCiclo" class="period-pill">{{ ultimoCiclo.nome }} · {{ dataCurta(ultimoCiclo.inicioEm) }}</span>
        </div>
        <div class="stat-grid">
          <div class="stat-card">
            <span class="stat-icon blue"><b-icon icon="account-group-outline" /></span>
            <strong>{{ membros.length }}</strong>
            <span>Integrantes atuais</span>
            <small>vinculados à equipe</small>
          </div>
          <div class="stat-card">
            <span class="stat-icon violet"><b-icon icon="chart-line" /></span>
            <strong>{{ ultimoCiclo ? formatarNota(ultimoCiclo.media) : '—' }}</strong>
            <span>Média do diagnóstico</span>
            <small v-if="penultimoCiclo">{{ textoVariacao(ultimoCiclo.media - penultimoCiclo.media) }} em relação ao ciclo anterior</small>
            <small v-else>{{ ultimoCiclo ? 'último ciclo divulgado' : 'aguardando ciclo divulgado' }}</small>
          </div>
          <div class="stat-card">
            <span class="stat-icon mint"><b-icon icon="clipboard-check-outline" /></span>
            <strong>{{ pdisAtivos }}</strong>
            <span>PDIs em andamento</span>
            <small>planos da equipe</small>
          </div>
          <div class="stat-card">
            <span class="stat-icon peach"><b-icon icon="flag-checkered" /></span>
            <strong>{{ metas.length }}</strong>
            <span>Metas acompanhadas</span>
            <small>atribuídas à equipe</small>
          </div>
        </div>
      </section>

      <section v-if="dadosCarregados" class="insight-grid">
        <div class="dimension-card">
          <div class="card-heading">
            <div>
              <p class="eyebrow">EVOLUÇÃO DO DIAGNÓSTICO</p>
              <h2>As dimensões da equipe</h2>
              <p v-if="penultimoCiclo">Comparação dos dois últimos ciclos com resultado divulgado.</p>
              <p v-else>Notas do último ciclo com resultado divulgado.</p>
            </div>
            <router-link class="text-link" to="/rh/diagnostico">Ver diagnóstico <b-icon icon="arrow-right" size="is-small" /></router-link>
          </div>
          <div v-if="ultimoCiclo && dimensoesOrdenadas.length" class="dimension-list">
            <div class="chart-legend">
              <span><i class="legend-bar"></i>{{ ultimoCiclo.nome }}</span>
              <span v-if="penultimoCiclo"><i class="legend-mark"></i>{{ penultimoCiclo.nome }}</span>
            </div>
            <div v-for="dimensao in dimensoesOrdenadas" :key="dimensao.id" class="dimension-row">
              <span class="dimension-name">{{ dimensao.nome }}</span>
              <div class="dimension-track" :aria-label="`${dimensao.nome}: ${formatarNota(dimensao.media)} de 5`">
                <span :style="{ width: larguraNota(dimensao.media) }"></span>
                <i v-if="penultimoCiclo && notaAnterior(dimensao.id) !== null" :style="{ left: larguraNota(notaAnterior(dimensao.id)) }"></i>
              </div>
              <strong>{{ formatarNota(dimensao.media) }}</strong>
              <small v-if="penultimoCiclo && notaAnterior(dimensao.id) !== null" :class="{ positive: dimensao.media >= notaAnterior(dimensao.id), negative: dimensao.media < notaAnterior(dimensao.id) }">{{ textoVariacao(dimensao.media - notaAnterior(dimensao.id)) }}</small>
            </div>
            <p class="chart-note">Escala de 1 a 5. A marca vertical indica a nota do ciclo anterior.</p>
          </div>
          <div v-else class="empty-state">As médias aparecem aqui após o encerramento de um ciclo com o mínimo de respostas.</div>
        </div>

        <aside class="focus-card">
          <p class="eyebrow">ONDE OLHAR AGORA</p>
          <h2>Próximo passo da liderança</h2>
          <div v-if="dimensaoPrioritaria" class="focus-highlight">
            <span class="focus-label">DIMENSÃO COM MENOR MÉDIA</span>
            <strong>{{ dimensaoPrioritaria.nome }}</strong>
            <span class="focus-score">{{ formatarNota(dimensaoPrioritaria.media) }} <small>de 5</small></span>
            <p>Use esse dado agregado para abrir uma conversa com a equipe e definir uma ação concreta.</p>
          </div>
          <div v-else class="focus-highlight">
            <strong>Comece pela escuta.</strong>
            <p>Quando houver um ciclo encerrado com respostas suficientes, as dimensões aparecerão aqui.</p>
          </div>
          <div class="focus-followup">
            <span><b-icon icon="calendar-check-outline" size="is-small" /> {{ proximosEncontros }} {{ proximosEncontros === 1 ? 'próximo 1:1' : 'próximos 1:1' }}</span>
            <span><b-icon icon="alert-circle-outline" size="is-small" /> {{ pdisAtrasados }} {{ pdisAtrasados === 1 ? 'PDI atrasado' : 'PDIs atrasados' }}</span>
          </div>
          <router-link class="focus-action" to="/rh/desenvolvimento">Acompanhar desenvolvimento <b-icon icon="arrow-right" size="is-small" /></router-link>
        </aside>
      </section>
      <section v-if="dadosCarregados" class="leader-seven-section">
        <div v-if="pendentes7D.length" class="leader-seven-pending">
          <strong>Autoavaliação 7D pendente</strong>
          <router-link :to="{ path: '/rh/diagnostico', query: { unidadeId: String(unidadeId), cicloId: String(pendentes7D[0].id), tipo: '7d' } }">Responder agora <b-icon icon="arrow-right" size="is-small" /></router-link>
        </div>
        <div v-if="resultado7D" class="leader-seven-layout">
          <RhResultado7D :resultado="resultado7D" />
          <aside v-if="resultado7D.dimensoes.length" class="leader-seven-insights" aria-label="Indicadores da avaliação 7D">
            <div class="leader-seven-insights-card">
              <p v-if="!resultado7D.autoRespondida" class="leader-seven-insights-empty">Aguardando sua autoavaliação para calcular as diferenças.</p>
              <template v-else>
              <h3>Pontos cegos</h3>
              <p>Você se avalia 0,6 ponto ou mais acima do que a equipe percebe.</p>
              <div v-if="pontosCegos7D.length" class="leader-seven-tags">
                <span v-for="dimensao in pontosCegos7D" :key="dimensao.id" class="leader-seven-tag blind">
                  {{ dimensao.nome }} {{ textoVariacao(dimensao.diferenca) }}
                </span>
              </div>
              <p v-else class="leader-seven-insights-empty">Nenhum ponto cego identificado neste ciclo.</p>

              <h3>Alinhados com a equipe</h3>
              <p>Diferença de até 0,3 ponto entre sua autoavaliação e a visão da equipe.</p>
              <div v-if="alinhados7D.length" class="leader-seven-tags">
                <span v-for="dimensao in alinhados7D" :key="dimensao.id" class="leader-seven-tag aligned">
                  {{ dimensao.nome }} {{ textoVariacao(dimensao.diferenca) }}
                </span>
              </div>
              <p v-else class="leader-seven-insights-empty">Nenhuma dimensão nesta faixa neste ciclo.</p>
              </template>
            </div>
          </aside>
        </div>
        <div v-else class="empty-state">Sua avaliação 7D aparecerá após um ciclo encerrado com o mínimo de respostas da equipe.</div>
      </section>
    </template>
  </main>
</template>

<script>
import http from '@/http'
import RhCabecalho from '@/components/rh_cabecalho.vue'
import RhResultado7D from '@/components/rh_resultado_7d.vue'

export default {
  name: 'RhDashboardLider',
  components: { RhCabecalho, RhResultado7D },
  data() {
    return {
      empresas: [],
      unidadeId: null,
      equipes: [],
      equipeId: null,
      equipesCarregadas: false,
      dadosCarregados: false,
      ciclos: [],
      pendentes7D: [],
      resultado7D: null,
      evolucao: [],
      membros: [],
      pdis: [],
      reunioes: [],
      metas: [],
      minimoRespostas: null,
      loading: false,
      erro: '',
      requisicao: 0,
    }
  },
  computed: {
    primeiroNome() {
      const usuario = this.$store.state.usuario || {}
      const nome = usuario.apelido || usuario.funcionarioNome || usuario.nomeCadastro || usuario.nome || 'você'
      return String(nome).trim().split(/\s+/)[0]
    },
    equipeAtual() {
      return this.equipes.find((equipe) => equipe.id === this.equipeId) || null
    },
    cicloAberto() {
      return this.ciclos.find((ciclo) => ciclo.ativo) || this.ciclos.find((ciclo) => ciclo.aberto) || null
    },
    progressoMinimo() {
      return this.cicloAberto && this.minimoRespostas
        ? Math.min(100, Math.round((this.cicloAberto.respostas / this.minimoRespostas) * 100))
        : 0
    },
    ultimoCiclo() {
      return this.evolucao[this.evolucao.length - 1] || null
    },
    penultimoCiclo() {
      return this.evolucao[this.evolucao.length - 2] || null
    },
    dimensoesOrdenadas() {
      return this.ultimoCiclo ? [...this.ultimoCiclo.dimensoes].sort((a, b) => a.media - b.media) : []
    },
    dimensaoPrioritaria() {
      return this.dimensoesOrdenadas[0] || null
    },
    dimensoesComDiferenca7D() {
      if (!this.resultado7D || !this.resultado7D.autoRespondida) return []
      return (this.resultado7D.dimensoes || []).filter((dimensao) =>
        dimensao.diferenca !== null && dimensao.diferenca !== undefined && Number.isFinite(Number(dimensao.diferenca))
      )
    },
    pontosCegos7D() {
      return this.dimensoesComDiferenca7D
        .filter((dimensao) => this.diferencaArredondada7D(dimensao.diferenca) <= -0.6)
        .sort((a, b) => Number(a.diferenca) - Number(b.diferenca))
    },
    alinhados7D() {
      return this.dimensoesComDiferenca7D
        .filter((dimensao) => Math.abs(this.diferencaArredondada7D(dimensao.diferenca)) <= 0.3)
        .sort((a, b) => a.ordem - b.ordem)
    },
    pdisAtivos() {
      return this.pdis.filter((pdi) => pdi.status !== 'concluido' && pdi.status !== 'cancelado').length
    },
    pdisAtrasados() {
      return this.pdis.filter((pdi) =>
        pdi.status !== 'concluido' && pdi.status !== 'cancelado' && new Date(pdi.prazo).getTime() < Date.now()
      ).length
    },
    proximosEncontros() {
      return this.reunioes.filter((reuniao) =>
        reuniao.status !== 'cancelada' && reuniao.proximaData && new Date(reuniao.proximaData).getTime() >= Date.now()
      ).length
    },
  },
  created() {
    this.iniciar()
  },
  methods: {
    dataCurta(valor) {
      return valor ? new Date(valor).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
    },
    formatarNota(valor) {
      return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    },
    textoVariacao(valor) {
      const numero = Number(valor)
      return `${numero > 0 ? '+' : ''}${numero.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`
    },
    diferencaArredondada7D(valor) {
      const numero = Number(valor)
      return Math.sign(numero) * Math.round(Math.abs(numero) * 10) / 10
    },
    larguraNota(valor) {
      return `${Math.max(0, Math.min(100, (Number(valor) / 5) * 100))}%`
    },
    notaAnterior(id) {
      const dimensao = this.penultimoCiclo && this.penultimoCiclo.dimensoes.find((item) => item.id === id)
      return dimensao ? Number(dimensao.media) : null
    },
    selecionarEmpresa(id) {
      this.unidadeId = id
      this.carregarEquipes()
    },
    async iniciar() {
      this.loading = true
      try {
        this.empresas = (await http.get('/api/rh/estrutura/empresas')).data || []
        const atual = Number(this.$store.state.unidadeId)
        this.unidadeId = this.empresas.some((empresa) => empresa.id === atual)
          ? atual
          : (this.empresas[0] && this.empresas[0].id) || null
        if (this.unidadeId) await this.carregarEquipes()
      } catch (e) {
        this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível carregar seu painel.'
      } finally {
        this.loading = false
      }
    },
    async carregarEquipes() {
      const requisicao = ++this.requisicao
      const unidadeId = this.unidadeId
      this.loading = true
      this.erro = ''
      this.equipes = []
      this.equipeId = null
      this.equipesCarregadas = false
      this.limparDados()
      if (!unidadeId) {
        this.loading = false
        return
      }
      try {
        const { data } = await http.get('/api/rh/estrutura/equipes', { params: { unidadeId } })
        if (requisicao !== this.requisicao) return
        this.equipes = data || []
        this.equipesCarregadas = true
        this.equipeId = (this.equipes[0] && this.equipes[0].id) || null
        if (this.equipeId) await this.carregarEquipe()
      } catch (e) {
        if (requisicao === this.requisicao)
          this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível carregar suas equipes.'
      } finally {
        if (requisicao === this.requisicao) this.loading = false
      }
    },
    limparDados() {
      this.dadosCarregados = false
      this.ciclos = []
      this.pendentes7D = []
      this.resultado7D = null
      this.evolucao = []
      this.membros = []
      this.pdis = []
      this.reunioes = []
      this.metas = []
      this.minimoRespostas = null
    },
    async carregarEquipe() {
      const requisicao = ++this.requisicao
      const unidadeId = this.unidadeId
      const equipeId = this.equipeId
      this.loading = true
      this.erro = ''
      this.limparDados()
      if (!unidadeId || !equipeId) {
        this.loading = false
        return
      }
      try {
        const params = { unidadeId, equipeId }
        const [ciclos, evolucao, membros, pdis, reunioes, metas, politica, resultado7D, pendentes] = await Promise.all([
          http.get('/api/rh/diagnostico/ciclos', { params }),
          http.get('/api/rh/diagnostico/evolucao', { params }),
          http.get('/api/rh/estrutura/membros', { params }),
          http.get('/api/rh/desenvolvimento/pdis', { params }),
          http.get('/api/rh/desenvolvimento/reunioes', { params }),
          http.get('/api/rh/desenvolvimento/metas', { params }),
          http.get('/api/rh/diagnostico/minimo-respostas', { params: { unidadeId } }),
          http.get('/api/rh/diagnostico/ultimo-resultado-lider-7d', { params }),
          http.get('/api/rh/diagnostico/meus-ciclos', { params: { unidadeId } }),
        ])
        if (requisicao !== this.requisicao) return
        this.ciclos = ciclos.data || []
        this.resultado7D = resultado7D.data || null
        this.pendentes7D = (pendentes.data || []).filter((item) => item.tipo === '7d' && this.ciclos.some((ciclo) => ciclo.id === item.id))
        this.evolucao = evolucao.data || []
        this.membros = membros.data || []
        this.pdis = pdis.data || []
        this.reunioes = reunioes.data || []
        this.metas = metas.data || []
        this.minimoRespostas = politica.data && politica.data.minimoRespostas
        this.dadosCarregados = true
      } catch (e) {
        if (requisicao === this.requisicao)
          this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível carregar os indicadores desta equipe.'
      } finally {
        if (requisicao === this.requisicao) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.leader-page { --navy: #0c193a; --blue: #087cf4; --ink: #17223c; background: #f7f8fc; color: var(--ink); font-family: Montserrat, Arial, sans-serif; min-height: 100vh; padding: 0 28px 64px; border-radius: 18px; }
.welcome { display: flex; justify-content: space-between; align-items: center; gap: 30px; background: radial-gradient(circle at 85% 10%, #1b437c 0, transparent 34%), linear-gradient(125deg, #0c193a, #10234e); color: #fff; border-radius: 0 0 30px 30px; padding: 40px 44px; }
.welcome-kicker { color: #9dc3ff; font-size: 11px; font-weight: 800; letter-spacing: .16em; margin: 0 0 8px; }
.welcome h1 { color: #fff; font-size: clamp(34px, 4vw, 56px); line-height: 1.1; letter-spacing: -.04em; margin: 0 0 12px; font-weight: 800; }
.welcome h1 span { color: #55c7dd; }
.welcome-intro > p:last-child { color: #d8e6ff; font-size: 15px; margin: 0; max-width: 570px; }
.welcome-team { display: flex; align-items: center; gap: 15px; min-width: 255px; background: rgba(255,255,255,.09); border: 1px solid rgba(188,213,255,.24); border-radius: 20px; padding: 17px 20px; }
.team-icon { color: #a9d5ff; background: rgba(255,255,255,.1); border-radius: 12px; padding: 10px; }
.welcome-team div { display: flex; flex-direction: column; gap: 2px; }
.welcome-team small { color: #a7caff; font-size: 10px; font-weight: 800; letter-spacing: .12em; }
.welcome-team strong { color: #fff; font-size: 17px; }
.welcome-team div > span { color: #c5d9f5; font-size: 12px; }
.team-selector { max-width: 450px; margin: 24px 0; }
.collection-card { display: flex; align-items: center; gap: 28px; background: #10214b; color: #fff; border-radius: 20px; padding: 24px 28px; margin: 28px 0 36px; }
.collection-copy { flex: 1; }
.collection-kicker { color: #91c5ff; font-size: 11px; font-weight: 800; letter-spacing: .12em; margin: 0 0 5px; }
.collection-copy h2 { color: #fff; font-size: 18px; font-weight: 800; margin: 0 0 5px; }
.collection-copy > p:last-child { color: #c9dafa; font-size: 12px; line-height: 1.6; margin: 0; }
.collection-copy > p span { display: block; }
.collection-progress { flex: 0 1 230px; }
.progress-track { height: 9px; background: #2c4066; border-radius: 20px; overflow: hidden; }
.progress-track span { display: block; height: 100%; background: #168bff; border-radius: inherit; }
.collection-progress small { color: #accaf5; font-size: 11px; }
.collection-action { color: #fff; border: 1px solid #6a83b0; border-radius: 30px; padding: 11px 17px; white-space: nowrap; font-size: 12px; font-weight: 800; }
.collection-action:hover { color: #fff; background: #1e3b76; }
.collection-action .icon, .text-link .icon, .focus-action .icon, .focus-followup .icon { vertical-align: middle; }
.section-heading, .card-heading { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; margin-bottom: 21px; }
.eyebrow { color: #2280ca; font-size: 11px; font-weight: 800; letter-spacing: .16em; margin: 0 0 6px; }
.section-heading h2, .card-heading h2 { color: var(--navy); font-size: clamp(23px, 2.6vw, 32px); font-weight: 800; line-height: 1.2; margin: 0 0 5px; }
.section-heading p:last-child, .card-heading p:last-child { color: #6b778e; font-size: 13px; margin: 0; }
.period-pill { color: #3b5782; background: #e9f0fb; border-radius: 30px; font-size: 11px; font-weight: 700; padding: 8px 13px; }
.stat-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.stat-card { display: flex; flex-direction: column; min-height: 178px; background: #fff; border: 1px solid #e3e8f0; border-radius: 20px; padding: 22px; box-shadow: 0 10px 24px rgba(16,32,68,.04); }
.stat-icon { display: grid; place-items: center; width: 39px; height: 39px; border-radius: 12px; margin-bottom: auto; }
.stat-icon.blue { color: #1b76d9; background: #e5f1ff; } .stat-icon.violet { color: #714be2; background: #efeaff; } .stat-icon.mint { color: #168b7c; background: #ddf8f1; } .stat-icon.peach { color: #bd6a27; background: #fff0e1; }
.stat-card strong { color: var(--navy); font-size: 34px; line-height: 1.1; font-weight: 800; margin: 16px 0 5px; }
.stat-card > span:not(.stat-icon) { color: var(--ink); font-size: 13px; font-weight: 700; }
.stat-card small { color: #778398; font-size: 11px; margin-top: 4px; }
.insight-grid { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(300px, .85fr); align-items: stretch; gap: 18px; margin-top: 24px; }
.dimension-card { background: #fff; border: 1px solid #e3e8f0; border-radius: 22px; padding: 28px; box-shadow: 0 10px 24px rgba(16,32,68,.04); min-width: 0; }
.card-heading { align-items: start; }
.card-heading h2 { font-size: 23px; }
.text-link { color: #126fd3; font-size: 12px; font-weight: 800; white-space: nowrap; }
.text-link:hover { color: #0a4390; }
.chart-legend { display: flex; flex-wrap: wrap; gap: 18px; border-bottom: 1px solid #e7ebf1; padding-bottom: 15px; color: #57647b; font-size: 11px; }
.chart-legend span { display: flex; align-items: center; gap: 6px; }
.legend-bar { width: 13px; height: 7px; background: #168bff; border-radius: 5px; }
.legend-mark { width: 2px; height: 13px; background: #0d2046; }
.dimension-row { display: grid; grid-template-columns: minmax(115px, 1.2fr) minmax(100px, 1fr) 36px 44px; align-items: center; gap: 12px; border-bottom: 1px solid #edf0f4; padding: 14px 0; }
.dimension-name { color: var(--navy); font-size: 12px; font-weight: 700; }
.dimension-track { position: relative; height: 9px; background: #e9edf2; border-radius: 20px; }
.dimension-track > span { display: block; height: 100%; background: #168bff; border-radius: inherit; }
.dimension-track > i { position: absolute; top: -3px; width: 2px; height: 15px; background: #0d2046; }
.dimension-row strong { color: var(--navy); font-size: 13px; }
.dimension-row small { color: #778398; font-size: 11px; }
.dimension-row small.positive { color: #087f69; } .dimension-row small.negative { color: #d05346; }
.chart-note { color: #8792a3; font-size: 11px; margin: 13px 0 0; }
.focus-card { display: flex; flex-direction: column; background: #0e1d44; color: #fff; border-radius: 22px; padding: 28px; }
.focus-card .eyebrow { color: #9dc3ff; }
.focus-card h2 { color: #fff; font-size: 25px; line-height: 1.2; font-weight: 800; margin: 0 0 20px; }
.focus-highlight { display: flex; flex-direction: column; background: #192d57; border: 1px solid #3a527b; border-radius: 18px; padding: 21px; }
.focus-label { color: #9dc3ff; font-size: 10px; font-weight: 800; letter-spacing: .12em; margin-bottom: 7px; }
.focus-highlight strong { color: #fff; font-size: 20px; }
.focus-score { color: #78d6cb; font-size: 29px; font-weight: 800; margin-top: 8px; }
.focus-score small { color: #b5cbe9; font-size: 12px; font-weight: 500; }
.focus-highlight p { color: #d1def5; font-size: 13px; line-height: 1.6; margin: 12px 0 0; }
.focus-followup { display: flex; flex-wrap: wrap; gap: 10px; margin: 20px 0; }
.focus-followup span { display: inline-flex; align-items: center; gap: 5px; color: #cbdcf7; background: #1a305d; border-radius: 30px; padding: 9px 11px; font-size: 11px; }
.focus-action { color: #fff; background: #087cf4; border-radius: 13px; text-align: center; padding: 13px 15px; font-size: 12px; font-weight: 800; margin-top: auto; }
.focus-action:hover { color: #fff; background: #2991ff; }
.leader-seven-section { margin-top: 24px; }
.leader-seven-pending { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 16px; padding: 16px 20px; border-radius: 16px; background: #e8f1ff; color: #0c326b; }
.leader-seven-pending a { color: #086bd2; font-weight: 800; }
.leader-seven-layout { display: grid; grid-template-columns: minmax(0, 1.45fr) minmax(320px, 1fr); align-items: start; gap: 18px; }
.leader-seven-layout > * { min-width: 0; }
.leader-seven-insights { background: #f2f1ee; border-radius: 24px; padding: 20px; }
.leader-seven-insights-card { background: #fff; border-radius: 20px; padding: 26px 28px; box-shadow: 0 10px 24px rgba(16,32,68,.04); }
.leader-seven-insights h3 { color: var(--navy); font-size: 18px; font-weight: 800; margin: 0 0 8px; }
.leader-seven-insights h3:not(:first-child) { margin-top: 24px; }
.leader-seven-insights p { color: #4e5b75; font-size: 13px; line-height: 1.55; margin: 0 0 15px; }
.leader-seven-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.leader-seven-tag { display: inline-flex; align-items: center; border-radius: 30px; padding: 6px 13px; font-size: 13px; font-weight: 800; line-height: 1.3; }
.leader-seven-tag.blind { color: #b72e1d; background: #fce9e6; }
.leader-seven-tag.aligned { color: #08745f; background: #e2f4ee; }
.leader-seven-insights .leader-seven-insights-empty { color: #7b8798; margin: 0; }
.empty-state { background: #fff; color: #66748a; border: 1px dashed #cfd8e6; border-radius: 18px; padding: 25px; text-align: center; font-size: 14px; }
.unlinked-state { max-width: 670px; margin: 34px auto; background: #fff; border: 1px solid #e3e8f0; border-radius: 24px; text-align: center; padding: 38px; box-shadow: 0 10px 24px rgba(16,32,68,.04); }
.unlinked-icon { display: inline-grid; place-items: center; width: 70px; height: 70px; margin-bottom: 16px; color: #326db4; background: #eaf2ff; border-radius: 20px; }
.unlinked-state h2 { color: var(--navy); font-size: 24px; font-weight: 800; margin: 0 0 10px; }
.unlinked-state > p:last-child { color: #66748a; line-height: 1.65; margin: 0; }
@media (max-width: 1100px) { .leader-seven-layout { grid-template-columns: 1fr; } }
@media (max-width: 1050px) { .stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .insight-grid { grid-template-columns: 1fr; } }
@media (max-width: 700px) { .welcome { flex-direction: column; align-items: stretch; padding: 27px 22px; } .welcome-team { min-width: 0; } .collection-card { flex-wrap: wrap; } .collection-progress { flex: 1 1 100%; } .collection-action { width: 100%; text-align: center; } .card-heading, .section-heading { flex-direction: column; align-items: flex-start; } }
@media (max-width: 600px) { .leader-page { padding: 0 14px 36px; } .welcome { border-radius: 0 0 26px 26px; } .collection-card, .dimension-card, .focus-card { padding: 20px; } .leader-seven-insights { padding: 12px; } .leader-seven-insights-card { padding: 20px; } .stat-grid { gap: 10px; } .stat-card { min-height: 159px; padding: 16px; } .dimension-row { grid-template-columns: minmax(80px, 1fr) minmax(70px, 1fr) 31px 37px; gap: 7px; } .dimension-name { font-size: 11px; } }
@media (max-width: 380px) { .stat-grid { grid-template-columns: 1fr; } }
</style>
